import { useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router';

import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  Container,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  styled
} from '@mui/material';

import Button from '@/components/button';
import Header from '@/components/header';
import Post from '@/components/post';
import { CreatorPostVisualsProvider } from '@/contexts/CreatorDetailsContext';
import { useFetchCreatorsPosts } from '@/hooks/queries/creators';
import { useFetchPost } from '@/hooks/queries/posts';
import useArrowKeyNavigation from '@/hooks/useArrowKeyNavigation';
import useMedia from '@/hooks/useMedia';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import shadows from '@/themes/theme/shadows';
import getRoute from '@/utils/getRoute';
import Error from '@/views/misc/error';
import NotFound from '@/views/misc/notFound';

import CreatorPostKeywordsDetails from '../_partials/CreatorPostKeywordsDetails';
import CreatorPostLabels from '../_partials/CreatorPostLabels';
import CreatorPostTranscript from '../_partials/CreatorPostTranscript';
import CreatorPostVisuals from '../_partials/CreatorPostVisuals';

const ScrollableStack = styled(Stack)(({ theme }) => ({
  display: 'block',
  maxHeight: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '16px',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.background.paper,
  boxShadow: shadows.cardDefault,
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.paper}`,
  '& > *': {
    border: `1px solid ${theme.palette.divider}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out'
  }
}));

const CreatorPostSkeleton = () => (
  <Grid container spacing={2} sx={{ height: 'calc(100vh - 108px)' }}>
    <Grid item xs={12} md={8} sx={{ height: '100%' }}>
      <Stack gap={2} sx={{ height: '100%' }}>
        <Skeleton variant="rectangular" height={75.25} />
        <Skeleton variant="rectangular" height="calc(100% - 72px)" />
      </Stack>
    </Grid>
    <Grid item xs={12} md={4} sx={{ height: '100%' }}>
      <Skeleton variant="rectangular" height="100%" />
    </Grid>
  </Grid>
);

type CreatorPostsPage = {
  results: { id: number }[];
  totalItems?: number;
};

const targetPage = ({
  page,
  targetPostId,
  currentPage,
  totalPages,
  pageSize
}: {
  page: CreatorPostsPage;
  targetPostId: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}): number => {
  const { results, totalItems } = page;

  if (totalItems && results.length > 0) {
    const highestId = results[0].id;
    const lowestId = results[results.length - 1].id;
    const postsPerPage = highestId - lowestId + 1;
    if (postsPerPage > 0) {
      return Math.max(
        1,
        Math.min(
          totalPages,
          Math.ceil((highestId - targetPostId) / postsPerPage) + currentPage
        )
      );
    }
  }

  const estimated = Math.ceil(targetPostId / pageSize);
  return estimated > 0 ? estimated : currentPage + 1;
};

const CreatorPost = () => {
  const { postId, id: creatorId } = useParams();
  const navigate = useWorkspaceNavigate();

  // Convert to numbers and handle potential NaN values
  const numericPostId = Number(postId);
  const numericCreatorId = Number(creatorId);

  const {
    activeIndex,
    setActiveIndex,
    videoPlaybackStart,
    setVideoPlaybackStart,
    videoLastClick,
    setVideoLastClick,
    setVideoPlayedSeconds,
    showIdentificationMarkers,
    darkenImageStyle,
    markerItems,
    selectedMinorItems,
    setSelectedMinorItems,
    selectedOcrAdvItems,
    setSelectedOcrAdvItems,
    resetMarkers,
    resetMediaState
  } = useMedia();

  const {
    data: postData,
    isPending: isPostPending,
    error
  } = useFetchPost({ id: numericPostId });

  // State to track navigation within the paginated data
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // Fetch creator posts with pagination
  const {
    data: creatorPosts,
    isPending: isCreatorPostsPending,
    refetch: refetchCreatorPosts
  } = useFetchCreatorsPosts({
    id: numericCreatorId,
    params: { page: currentPage, size: PAGE_SIZE }
  });

  const totalPages = creatorPosts?.totalItems
    ? Math.ceil(creatorPosts.totalItems / PAGE_SIZE)
    : 1;

  const MAX_PAGE_SEARCH_ATTEMPTS = 4;

  const [prevPostId, setPrevPostId] = useState(numericPostId);
  const [triedPages, setTriedPages] = useState<number[]>([]);

  // Reset the search budget when the target post changes.
  const isNewPost = numericPostId !== prevPostId;
  if (isNewPost) {
    setPrevPostId(numericPostId);
    setTriedPages([]);
  }
  const checkedPages = isNewPost ? [] : triedPages;

  const postFoundOnPage =
    creatorPosts?.results?.some((post) => post.id === numericPostId) ?? false;

  if (
    !isCreatorPostsPending &&
    creatorPosts?.results &&
    !postFoundOnPage &&
    checkedPages.length < MAX_PAGE_SEARCH_ATTEMPTS
  ) {
    const nextPage = targetPage({
      page: creatorPosts,
      targetPostId: numericPostId,
      currentPage,
      totalPages,
      pageSize: PAGE_SIZE
    });

    if (!checkedPages.includes(nextPage)) {
      setTriedPages([...checkedPages, nextPage]);
      setCurrentPage(nextPage);
    }
  }

  const orderedPostIds = useMemo(
    () => creatorPosts?.results?.map((post) => post.id) || [],
    [creatorPosts]
  );

  const currentIndex = orderedPostIds.indexOf(numericPostId);
  const canNavigatePrevious = currentIndex > 0 || currentPage > 1;
  const canNavigateNext =
    (currentIndex >= 0 && currentIndex < orderedPostIds.length - 1) ||
    currentPage < totalPages;

  const handlePreviousPost = useCallback(() => {
    resetMediaState();
    if (currentIndex > 0) {
      // Simple case: previous post is in the current page
      navigate(
        getRoute.creators.POST(
          numericCreatorId,
          orderedPostIds[currentIndex - 1]
        )
      );
    } else if (currentPage > 1) {
      // Need to go to previous page and select the last post
      setCurrentPage((prev) => prev - 1);
      refetchCreatorPosts().then((response) => {
        const posts = response.data?.results ?? [];
        if (posts.length > 0) {
          navigate(
            getRoute.creators.POST(numericCreatorId, posts[posts.length - 1].id)
          );
        }
      });
    }
  }, [
    currentIndex,
    currentPage,
    orderedPostIds,
    numericCreatorId,
    navigate,
    resetMediaState,
    refetchCreatorPosts
  ]);

  const handleNextPost = useCallback(() => {
    resetMediaState();
    if (currentIndex < orderedPostIds.length - 1) {
      // Simple case: next post is in the current page
      navigate(
        getRoute.creators.POST(
          numericCreatorId,
          orderedPostIds[currentIndex + 1]
        )
      );
    } else if (currentPage < totalPages) {
      // Need to go to next page and select the first post
      setCurrentPage((prev) => prev + 1);
      refetchCreatorPosts().then((response) => {
        const posts = response.data?.results ?? [];
        if (posts.length > 0) {
          navigate(getRoute.creators.POST(numericCreatorId, posts[0].id));
        }
      });
    }
  }, [
    currentIndex,
    currentPage,
    orderedPostIds,
    totalPages,
    numericCreatorId,
    navigate,
    resetMediaState,
    refetchCreatorPosts
  ]);

  const handleReturnToCreator = () => {
    navigate(getRoute.creators.DETAIL(numericCreatorId));
  };

  useArrowKeyNavigation({
    onLeft: () => {
      if (canNavigatePrevious) handlePreviousPost();
    },
    onRight: () => {
      if (canNavigateNext) handleNextPost();
    }
  });

  const hasRightPanelContent = useMemo(() => {
    if (!postData) return false;
    const hasKeywordDetails =
      postData.description ||
      postData.tags?.length ||
      postData.hashtags?.length;
    const hasTranscript = postData.media?.some(
      (m) => (m.transcript?.length ?? 0) > 0
    );
    const hasMinorsResults = postData.media?.some((m) =>
      m.visualRecognition?.some((r) => r.type === 'minors')
    );
    const hasOcrResults = postData.media?.some((m) =>
      m.visualRecognition?.some(
        (r) => (r.type === 'ocr-adv' || r.type === 'ocr') && r.items?.length > 0
      )
    );
    return (
      hasKeywordDetails || hasTranscript || hasMinorsResults || hasOcrResults
    );
  }, [postData]);

  if (!postId || Number.isNaN(numericPostId)) return <NotFound />;
  if (error) return <Error message="Failed to load post" error={error} />;

  const isPending = isPostPending || isCreatorPostsPending;

  if (!isPending && !postData) return <NotFound />;

  return (
    <CreatorPostVisualsProvider
      key={numericPostId}
      value={{
        selectedMinorItems,
        setSelectedMinorItems,
        selectedOcrAdvItems,
        setSelectedOcrAdvItems,
        markerItems,
        clearSelections: resetMarkers
      }}
    >
      <Container maxWidth={false}>
        <Header
          sx={{ pt: 3 }}
          mb
          isSmallTitle
          actions={
            <>
              <Tooltip title="Left arrow key for previous post" arrow>
                <span>
                  <Button
                    disabled={!canNavigatePrevious || isPending}
                    onClick={handlePreviousPost}
                    size="small"
                    startIcon={<ArrowBack />}
                    text="Previous"
                  />
                </span>
              </Tooltip>
              <Tooltip title="Right arrow key for next post" arrow>
                <span>
                  <Button
                    disabled={!canNavigateNext || isPending}
                    onClick={handleNextPost}
                    endIcon={<ArrowForward />}
                    size="small"
                    text="Next"
                  />
                </span>
              </Tooltip>
            </>
          }
          onClickReturn={handleReturnToCreator}
          showDivider
          title={
            postData?.creator?.name || <Skeleton variant="text" width="200px" />
          }
        />
        {isPending ? (
          <CreatorPostSkeleton />
        ) : (
          postData && (
            <Grid
              container
              spacing={2}
              sx={{
                height: { xs: 'auto', md: 'calc(100vh - 108px)' },
                overflow: { xs: 'visible', md: 'hidden' }
              }}
            >
              {/* Left: post viewer */}
              <Grid
                item
                xs={12}
                md={hasRightPanelContent ? 8 : 12}
                sx={{ maxHeight: '100%' }}
              >
                <Post
                  postData={postData}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  evaluationProps={{
                    showIdentificationMarkers,
                    markerItems,
                    videoPlaybackStart,
                    videoLastClick,
                    darkenImageStyle,
                    setVideoPlayedSeconds,
                    resetMarkers
                  }}
                />
              </Grid>
              {/* Right: details panel */}
              {hasRightPanelContent && (
                <Grid item xs={12} md={4} sx={{ maxHeight: '100%' }}>
                  <ScrollableStack>
                    <CreatorPostKeywordsDetails postData={postData} />
                    <CreatorPostLabels postData={postData} />
                    {postData?.media && (
                      <CreatorPostTranscript
                        media={postData.media}
                        activeIndex={activeIndex}
                        setActiveIndex={setActiveIndex}
                        videoPlaybackStart={videoPlaybackStart}
                        videoLastClick={videoLastClick}
                        setVideoPlaybackStart={setVideoPlaybackStart}
                        setVideoLastClick={setVideoLastClick}
                        resetMarkers={resetMarkers}
                      />
                    )}
                    {postData?.media && (
                      <CreatorPostVisuals
                        media={postData.media}
                        activeIndex={activeIndex}
                        setActiveIndex={setActiveIndex}
                        setVideoPlaybackStart={setVideoPlaybackStart}
                        setVideoLastClick={setVideoLastClick}
                        resetMarkers={resetMarkers}
                      />
                    )}
                  </ScrollableStack>
                </Grid>
              )}
            </Grid>
          )
        )}
      </Container>
    </CreatorPostVisualsProvider>
  );
};

export default CreatorPost;
